import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import MarketingReport from '../MarketingReport';
import ResponsiveReportView, { PDFLayout, PageSize, BrandingTheme } from './ResponsiveReportView';
import { Report, ReportData } from '../../types/reportTypes';
import { FilterState, ReportPeriod, ComparisonType, FilterLogic } from '../../types/filterTypes';
import { usePrintHook } from '../../hooks/usePrintHook';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ReportDetail: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useMockData, setUseMockData] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { printRef } = usePrintHook();

  // Get information from route state if available
  const navigationState = location.state as {
    isInteractive?: boolean;
    reportType?: 'monthly' | 'quarterly' | 'annual';
    reportPeriod?: { month: number; year: number };
  } | undefined;

  // Initial filter state based on report period
  const [filterState, setFilterState] = useState<FilterState>({
    reportPeriod: ReportPeriod.MONTHLY,
    monthPeriod: {
      month: report?.period?.month || navigationState?.reportPeriod?.month || new Date().getMonth() + 1,
      year: report?.period?.year || navigationState?.reportPeriod?.year || new Date().getFullYear()
    },
    comparisonType: ComparisonType.NONE,
    sources: [],
    campaigns: [],
    trades: [],
    metricThresholds: [],
    filterLogic: FilterLogic.AND,
    sortConfig: [],
    showZeroValues: true,
    expandedGroups: []
  });

  // Get month name from month number
  const getMonthName = (month: number): string => {
    return new Date(2000, month - 1).toLocaleString('default', { month: 'long' });
  };

  // Format report period based on report type
  const formatPeriod = (report: Report): string => {
    if (report.reportType === 'monthly') {
      return `${getMonthName(report.period.month)} ${report.period.year}`;
    } else if (report.reportType === 'quarterly') {
      const quarter = Math.ceil(report.period.month / 3);
      return `Q${quarter} ${report.period.year}`;
    } else {
      return `${report.period.year}`;
    }
  };
  
  // Update filter state when report changes
  useEffect(() => {
    if (report?.period) {
      setFilterState(prevState => ({
        ...prevState,
        reportPeriod: ReportPeriod.MONTHLY,
        monthPeriod: {
          month: report.period.month,
          year: report.period.year
        }
      }));
    }
  }, [report]);

  // Fetch report data
  useEffect(() => {
    const fetchReport = async () => {
      if (!reportId) return;

      setLoading(true);
      setError(null);

      try {
        // Check if we're using a sample ID
        if (reportId.startsWith('sample-')) {
          // Use mock data
          const mockData: ReportData = {
            kpis: {
              adSpend: 12500,
              totalSales: 167850,
              grossProfit: 75500,
              salesCommissions: 4850,
              managerFees: 1200,
              companyOH: 980,
              netProfit: 56470,
              netProfitPercent: 0.336,
              grossProfitPercent: 0.45
            },
            salesBySource: [
              { source: 'Google Ads', totalSales: 45600, count: 4, grossProfit: 20520 },
              { source: 'Facebook', totalSales: 36700, count: 3, grossProfit: 16515 },
              { source: 'Yelp', totalSales: 28400, count: 2, grossProfit: 12780 },
              { source: 'Organic Search', totalSales: 32350, count: 3, grossProfit: 14558 },
              { source: 'Referral', totalSales: 24800, count: 2, grossProfit: 11160 }
            ],
            adSpendBySource: [
              { source: 'Google Ads', spend: 5400 },
              { source: 'Facebook', spend: 4200 },
              { source: 'Yelp', spend: 1900 },
              { source: 'HomeAdvisor', spend: 1000 }
            ],
            salesByTrade: [
              { trade: 'Roofing', totalSales: 89500, count: 7, grossProfit: 40275 },
              { trade: 'Windows & Doors', totalSales: 54350, count: 5, grossProfit: 24458 },
              { trade: 'Siding', totalSales: 24000, count: 2, grossProfit: 10800 }
            ],
            jobData: [
              // Roofing jobs
              {
                jobInfo: 'William & Tyla Liggins - Roof Replacement',
                leadSource: 'Facebook',
                trade: 'Roofing',
                contractTotal: 26921,
                grossMarginPct: 0.45,
                grossProfit: 12114,
                salesCommission: 1076,
                salesManagerFee: 269,
                companyOH: 135,
                netProfit: 10634
              },
              {
                jobInfo: 'David & Gail Cantor - Roof Repair',
                leadSource: 'Referral',
                trade: 'Roofing',
                contractTotal: 18734,
                grossMarginPct: 0.46,
                grossProfit: 8618,
                salesCommission: 749,
                salesManagerFee: 187,
                companyOH: 94,
                netProfit: 7588
              },
              {
                jobInfo: 'Quinn & Jillian Gooden - Roof & Gutters',
                leadSource: 'Google Organic',
                trade: 'Roofing',
                contractTotal: 15685,
                grossMarginPct: 0.50,
                grossProfit: 7843,
                salesCommission: 627,
                salesManagerFee: 157,
                companyOH: 78,
                netProfit: 6981
              },
              {
                jobInfo: 'Rick & Donna Harrelson - Roof Replacement',
                leadSource: 'Google Ads',
                trade: 'Roofing',
                contractTotal: 11879,
                grossMarginPct: 0.43,
                grossProfit: 5108,
                salesCommission: 475,
                salesManagerFee: 119,
                companyOH: 59,
                netProfit: 4455
              },
              {
                jobInfo: 'Jason & Ellen Cruz - Roof Repair',
                leadSource: 'Yelp',
                trade: 'Roofing',
                contractTotal: 7450,
                grossMarginPct: 0.47,
                grossProfit: 3502,
                salesCommission: 298,
                salesManagerFee: 75,
                companyOH: 37,
                netProfit: 3092
              },
              {
                jobInfo: 'Eric & Allison Boiter - Roof Inspection & Repair',
                leadSource: 'Yelp',
                trade: 'Roofing',
                contractTotal: 4600,
                grossMarginPct: 0.365,
                grossProfit: 1679,
                salesCommission: 0,
                salesManagerFee: 46,
                companyOH: 23,
                netProfit: 1610
              },
              {
                jobInfo: 'Leslie Knapp - Emergency Leak Repair',
                leadSource: 'Previous Customer',
                trade: 'Roofing',
                contractTotal: 4231,
                grossMarginPct: 0.561,
                grossProfit: 2374,
                salesCommission: 0,
                salesManagerFee: 42,
                companyOH: 21,
                netProfit: 2311
              },
              
              // Windows & Doors jobs
              {
                jobInfo: 'Tara & Alfred Bell - Window Replacement (6 windows)',
                leadSource: 'Thumbtack',
                trade: 'Windows & Doors',
                contractTotal: 19700,
                grossMarginPct: 0.44,
                grossProfit: 8668,
                salesCommission: 788,
                salesManagerFee: 197,
                companyOH: 99,
                netProfit: 7584
              },
              {
                jobInfo: 'Tyler & Elea Fogarty - Front Door Replacement',
                leadSource: 'Angi',
                trade: 'Windows & Doors',
                contractTotal: 12350,
                grossMarginPct: 0.538,
                grossProfit: 6644,
                salesCommission: 494,
                salesManagerFee: 124,
                companyOH: 62,
                netProfit: 5964
              },
              {
                jobInfo: 'Sam & Rachel Winters - Window Replacement (4 windows)',
                leadSource: 'Google Ads',
                trade: 'Windows & Doors',
                contractTotal: 9400,
                grossMarginPct: 0.41,
                grossProfit: 3854,
                salesCommission: 376,
                salesManagerFee: 94,
                companyOH: 47,
                netProfit: 3337
              },
              {
                jobInfo: 'Charles & Diana Morrison - Sliding Door Installation',
                leadSource: 'Facebook',
                trade: 'Windows & Doors',
                contractTotal: 7500,
                grossMarginPct: 0.42,
                grossProfit: 3150,
                salesCommission: 300,
                salesManagerFee: 75,
                companyOH: 38,
                netProfit: 2737
              },
              {
                jobInfo: 'Hector & Gloria Ruiz - Storm Door Installation',
                leadSource: 'Google Ads',
                trade: 'Windows & Doors',
                contractTotal: 5400,
                grossMarginPct: 0.41,
                grossProfit: 2214,
                salesCommission: 216,
                salesManagerFee: 54,
                companyOH: 27,
                netProfit: 1917
              },
              
              // Siding jobs
              {
                jobInfo: 'Ken & Bonnie Davidson - Full Siding Replacement',
                leadSource: 'Facebook',
                trade: 'Siding',
                contractTotal: 16500,
                grossMarginPct: 0.45,
                grossProfit: 7425,
                salesCommission: 660,
                salesManagerFee: 165,
                companyOH: 83,
                netProfit: 6517
              },
              {
                jobInfo: 'Melinda & Dan Zhang - Partial Siding Repair',
                leadSource: 'Google Ads',
                trade: 'Siding',
                contractTotal: 7500,
                grossMarginPct: 0.45,
                grossProfit: 3375,
                salesCommission: 300,
                salesManagerFee: 75,
                companyOH: 38,
                netProfit: 2962
              }
            ]
          };
          
          const mockReport: Report = {
            id: reportId,
            title: reportId === 'sample-report-1' 
              ? 'March 2023 Marketing Performance' 
              : 'Q1 2023 Performance Summary',
            reportType: reportId === 'sample-report-1' ? 'monthly' : 'quarterly',
            period: { month: 3, year: 2023 },
            createdBy: 'sample-user',
            data: mockData,
            createdAt: new Date()
          };
          
          setReport(mockReport);
          setUseMockData(true);
        } else {
          // Fetch from API
          const fetchedReport = await apiService.getReport(reportId);
          setReport(fetchedReport);
          setUseMockData(false);
        }
      } catch (err) {
        console.error('Error fetching report:', err);
        setError('Failed to load report');
      } finally {
        setLoading(false);
      }
    };
    
    fetchReport();
  }, [reportId]);
  
  // Generate PDF from report
  const generatePDF = async () => {
    if (!reportRef.current) return;

    try {
      // Show loading state
      setLoading(true);

      // Create filename for the PDF
      const fileName = report
        ? `ROI_Marketing_Report_${formatPeriod(report).replace(/\s/g, '_')}.pdf`
        : 'marketing_report.pdf';

      // If using interactive view, try to find and click its export button
      if (navigationState?.isInteractive) {
        // Look for the "Export PDF" button within the ResponsiveReportView
        const exportButton = reportRef.current.querySelector('.pdf-export-button');
        if (exportButton) {
          // Trigger the click event on the export button
          (exportButton as HTMLElement).click();
          // The ResponsiveReportView will handle the PDF generation
          setLoading(false);
          return;
        }
      }

      // Fallback to standard PDF generation if not interactive or button not found
      const reportElement = reportRef.current;
      const canvas = await html2canvas(reportElement, {
        scale: 1.5, // Higher scale for better quality
        useCORS: true, // Allow images from other domains
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');

      // Page setup (portrait A4)
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Calculate ratio to fit content to PDF
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;

      // Add content spread across multiple pages if needed
      let heightLeft = contentHeight;
      let position = 0;
      let page = 1;

      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= pdfHeight;

      // Add more pages if content overflows
      while (heightLeft > 0) {
        position = -pdfHeight * page;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
        page++;
      }

      // Add footer on each page
      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }

      // Download PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle export to PDF
  const handleExportPDF = () => {
    generatePDF();
  };
  
  // Handle print
  const handlePrint = () => {
    if (reportRef.current) {
      // If using interactive view, try to find and click its print button
      if (navigationState?.isInteractive) {
        // Look for the "Print" button within the ResponsiveReportView
        const printButton = reportRef.current.querySelector('.print-report-button');
        if (printButton) {
          // Trigger the click event on the print button
          (printButton as HTMLElement).click();
          return;
        }
      }

      // Fallback to standard print if not interactive or button not found
      printRef(reportRef as unknown as React.RefObject<HTMLElement>);
    }
  };
  
  // Handle back button
  const handleBackClick = () => {
    navigate('/reports');
  };
  
  return (
    <div className="bg-white rounded-lg shadow p-4">
      {loading && !report ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-400 text-red-700 p-4 rounded mb-6">
          <h2 className="font-bold text-lg mb-2">Error</h2>
          <p>{error}</p>
          <button 
            onClick={handleBackClick}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
          >
            Back to Reports
          </button>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-6">
            <div>
              <button 
                onClick={handleBackClick}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100"
              >
                ← Back to Reports
              </button>
            </div>
            <div className="flex space-x-2">
              <button 
                onClick={handlePrint}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                </svg>
                Print
              </button>
              <button 
                onClick={handleExportPDF}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                Export PDF
              </button>
            </div>
          </div>
          
          {/* Show "Sample Report" indicator for mock data */}
          {useMockData && (
            <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 p-4 rounded mb-6">
              <p className="font-bold">Sample Report{navigationState?.isInteractive ? ' (Interactive)' : ''}</p>
              <p>This is a sample report for demonstration purposes.</p>
              {navigationState?.isInteractive && (
                <p className="mt-2">
                  <span className="font-semibold">Interactive features:</span> Filtering, pagination, and enhanced exports are available.
                </p>
              )}
            </div>
          )}
          
          {/* Report Content */}
          {report && (
            <div
              ref={reportRef}
              className="report-container"
              id="report-container"
            >
              {navigationState?.isInteractive ? (
                <ResponsiveReportView
                  data={report.data}
                  reportId={reportId}
                  filterState={filterState}
                  usePagination={report.data.jobData && report.data.jobData.length > 20}
                  pdfOptions={{
                    layout: PDFLayout.PORTRAIT,
                    pageSize: PageSize.LETTER,
                    brandingTheme: BrandingTheme.DEFAULT,
                    includeHeader: true,
                    includeFooter: true,
                    companyName: 'ROI Exteriors',
                    documentTitle: report.title
                  }}
                />
              ) : (
                // Fallback to standard report if not interactive
                <MarketingReport data={report.data} />
              )}
            </div>
          )}
        </>
      )}
      
      {/* Loading overlay for PDF generation */}
      {loading && report && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-center">Generating PDF...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDetail;